import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewEditWebsite from '@app/src/components/Drawer/DrawerViewEditWebsite'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import CountryCell from '@app/src/components/Table/Cells/CountryCell'
import TruncatedText from '@app/src/components/Ui/TruncatedText'
import type { Provider } from '@app/src/types/organizations'
import { normalizeWebsiteDisplay, validURL } from '@app/src/utils/helpers'
import { resolveProviderWebsiteAddress } from '@app/src/utils/providerEnrichmentUtils'
import { AddOutlined, AutoAwesomeOutlined, Edit, ExpandMoreOutlined } from '@mui/icons-material'
import type { AccordionProps } from '@mui/material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

type ProvidersAccordionConditionalCheckProps = {
  title: ReactNode
  providers: Array<Provider>
  selectedProviderIds: Array<number>
  onChange: (providerId: number) => void
  onWebsiteSaved?: (providerId: number) => void
  icon?: ReactNode
} & Omit<AccordionProps, 'onChange' | 'children'>

const ProvidersAccordionConditionalCheck: React.FC<ProvidersAccordionConditionalCheckProps> = ({
  title,
  providers,
  selectedProviderIds,
  onChange,
  onWebsiteSaved,
  icon,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()

  const openEditWebsiteDrawer = (provider: Provider) =>
    openDrawer(<DrawerViewEditWebsite provider={provider} onSaved={() => onWebsiteSaved?.(provider.id)} />, {
      showAboveDialogs: true,
    })

  return (
    <Accordion
      elevation={0}
      disableGutters
      {...props}
      sx={[{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }, ...[props.sx ?? []].flat()]}
    >
      <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
        <Stack direction="row" spacing={2}>
          {icon}
          <Typography>{title}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {providers.map(provider => {
            const providerWebsiteAddress = resolveProviderWebsiteAddress(provider)
            const displayWebsiteAddress = normalizeWebsiteDisplay(providerWebsiteAddress)
            const isValidWebsite = Boolean(providerWebsiteAddress)
            const websiteEnriched = isValidWebsite && !validURL(provider.websiteAddress)
            return (
              <ListItem
                key={provider.id}
                sx={{
                  justifyContent: 'space-between',
                  borderTop: `1px solid ${alpha('#000', 0.12)}`,
                }}
                disablePadding
              >
                <ListItemButton
                  selected={selectedProviderIds.includes(provider.id)}
                  onClick={() => {
                    if (!isValidWebsite) {
                      return
                    } else {
                      onChange(provider.id)
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1} width="40%" mr={1}>
                    {!isValidWebsite ? (
                      <Tooltip title={formatMessage({ id: 'form.scheduleProviderScreening.websiteMissing.tooltip' })}>
                        <Box>
                          <Checkbox disabled edge="start" checked={selectedProviderIds.includes(provider.id)} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Checkbox edge="start" checked={selectedProviderIds.includes(provider.id)} />
                    )}
                    <CompanyCell
                      width="95%"
                      company={provider}
                      disableCell
                      sx={{ width: '80%', minWidth: 0 }}
                      data-testid="accordion-company-name"
                    />
                  </Stack>

                  <Stack
                    mr={1}
                    sx={{
                      '& .MuiTableCell-root': {
                        height: 24,
                        padding: 0,
                        border: 'none',
                      },
                    }}
                    width="30%"
                  >
                    <CountryCell countryCode={provider.country?.countryCode} />
                  </Stack>

                  {!isValidWebsite ? (
                    <Stack direction="row" width="30%">
                      <Button
                        startIcon={<AddOutlined color="primary" />}
                        onClick={e => {
                          e.stopPropagation()
                          openEditWebsiteDrawer(provider)
                        }}
                      >
                        {formatMessage({ id: 'form.scheduleProviderScreening.websiteMissing.addWebsite' })}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" width="30%" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" width="90%" gap={1}>
                        {websiteEnriched && (
                          <Tooltip title={formatMessage({ id: 'general.aiEnriched' })}>
                            <AutoAwesomeOutlined sx={{ fontSize: 16, color: 'communication.dark' }} />
                          </Tooltip>
                        )}
                        <Stack width="90%">
                          <TruncatedText line={1} text={displayWebsiteAddress} />
                        </Stack>
                      </Stack>
                      <Stack width="10%">
                        <IconButton
                          onClick={e => {
                            e.stopPropagation()
                            openEditWebsiteDrawer(provider)
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

export default ProvidersAccordionConditionalCheck
